import React, { useState } from "react"
import styled, { css } from "styled-components"
import SwiperCore, { Pagination, Navigation } from "swiper"
import {
  AppFunctionComponent,
  RenderRichText,
  TextWithEmbedBuilder,
  TextWithEmbedLayout,
} from "../../../types"
import "swiper/swiper-bundle.min.css"

import { SectionContainer } from "../../../resolvers/text/text.resolver"
import StandardSection, {
  SectionHeading,
  SectionImage,
} from "../sections/section.component"
import TextWithEmbedResolver from "../../../resolvers/text-with-embed/text-with-embed.resolver"
import RichText from "../../../resolvers/rich-text/rich-text.resolver"

interface Props {
  slides: TextWithEmbedBuilder[]
  anchorId?: string
  isArrowsDisabled?: boolean
  text?: RenderRichText
}

SwiperCore.use([Pagination, Navigation])

const TextWithEmberSliderBorderNoImages: AppFunctionComponent<Props> = ({
  slides,
  text,
  anchorId,
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 1024

  return (
    <SliderContainer id={anchorId}>
      {text && (
        <StyledSectionText id="text-with-embed-slider-border-no-images">
          <SectionHeading>
            <RichText content={text} />
          </SectionHeading>
        </StyledSectionText>
      )}
      <StyledSliderContainer>
        <StyledSliderTitlesContainer>
          {slides.map((slide, index) => (
            <StyledTabWrapper key={index}>
              <StyledSliderTitle
                $active={index === activeIndex}
                id={`mobile-slide-normal-${index}`}
                onClick={() => {
                  setActiveIndex(index)
                  setTimeout(() => {
                    const element = document.getElementById(
                      `text-with-embed-slider-border-no-images`
                    )
                    if (element) {
                      const elementPosition =
                        element.getBoundingClientRect().top + window.scrollY
                      window.scrollTo({
                        top: elementPosition - 75,
                        behavior: "smooth",
                      })
                    }
                  }, 100)
                }}
              >
                {slide.title!}
              </StyledSliderTitle>
              {isMobile && index === activeIndex && (
                <StyledMobileContent>
                  <TextWithEmbedResolver
                    {...slides[index]}
                    layout={TextWithEmbedLayout.LeftText}
                    title={undefined}
                    embed={undefined}
                  />
                </StyledMobileContent>
              )}
            </StyledTabWrapper>
          ))}
        </StyledSliderTitlesContainer>
        {!isMobile && (
          <StyledSlideContainer>
            <TextWithEmbedResolver
              {...slides[activeIndex]}
              layout={TextWithEmbedLayout.LeftText}
              title={undefined}
              embed={undefined}
            />
          </StyledSlideContainer>
        )}
      </StyledSliderContainer>
    </SliderContainer>
  )
}

export default TextWithEmberSliderBorderNoImages

const SliderContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 80px 64px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    overflow: hidden;
    padding: 40px 20px;
  }
`

const StyledSectionText = styled(SectionContainer)`
  width: 100%;
  padding: 0 20px;
  ${SectionHeading} {
    grid-area: unset;
    h1 {
      color: #000;
      font-family: Facto;
      font-size: 37px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
    }
    p {
      color: #000;
      text-align: center;
      font-family: Montserrat;
      font-size: 18px;
      font-style: normal;
      font-weight: 300;
      line-height: 150%;
    }
    @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      h1 {
        color: #000;
        font-family: Facto;
        font-size: 30px;
        font-style: normal;
        font-weight: 400;
        line-height: 120%;
        text-align: left;
      }
      p {
        color: #000;
        font-family: Montserrat;
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: 150%;
        text-align: left;
      }
    }
  }
`

const StyledSliderContainer = styled.div`
  --max-width: 1260;
  width: calc(100% - 40px);
  border: 1px solid #abb0ba;
  max-width: calc(var(--max-width) * 1px);
  margin: 0 auto;
  border-radius: 12px;
  margin-top: 60px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    width: 100%;
    margin-top: 30px;
  }
`

const StyledSliderTitlesContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 70px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    flex-direction: column;
    height: auto;
  }
`

const StyledTabWrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    border-right: 1px solid #abb0ba;
    &:last-child {
      border-right: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    border-bottom: 1px solid #abb0ba;

    &:last-child {
      border-bottom: none;
    }
  }
`

const StyledSliderTitle = styled.button<{ $active: boolean }>`
  width: 100%;
  height: 100%;
  background: none;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #abb0ba;
  cursor: pointer;
  border-top: none;
  border-left: none;
  font-family: Facto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  color: #666;

  &:last-child {
    border-right: none;
  }

  ${({ $active }) =>
    $active &&
    css`
      border-bottom: none;
      color: black;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    height: 50px;
    justify-content: flex-start;
    padding-left: 20px;
    border: none;
    text-align: left;
  }
`

const StyledMobileContent = styled.div`
  & > div {
    padding: 0;
    grid-template-columns: 1fr;
    gap: 0;
    margin-top: 10px;
    margin-bottom: 16px;
  }

  ${StandardSection} {
    margin-top: 10px;
    margin-bottom: 16px;
    padding: 0;
  }

  ${SectionImage} {
    height: calc(100vw - 80px);
    img {
      height: calc(100vw - 80px);
    }
  }
`

const StyledSlideContainer = styled.div`
  padding: 48px 72px;

  div {
    grid-template-columns: 1fr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    padding: 24px 20px;

    div {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }
`
